import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
	HelpCircle,
	Search,
	BookOpen,
	Calendar,
	Shield,
	CreditCard,
	ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

interface FAQ {
	id: string;
	question: string;
	answer: string;
	user_role: "parent" | "doula";
	topic: "booking" | "communication" | "payment" | "safety" | "general";
	created_at: string;
	updated_at: string;
	is_active: boolean;
	created_by: string | null;
}

export default function ParentFAQ() {
	const [faqs, setFaqs] = useState<FAQ[]>([]);
	const [filteredFaqs, setFilteredFaqs] = useState<FAQ[]>([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedCategory, setSelectedCategory] = useState<string>("all");
	const { user } = useAuth();
	const navigate = useNavigate();

	// FAQ categories for better organization
	const categories = [
		{ id: "all", name: "All Questions", icon: BookOpen, count: 0 },
		{ id: "booking", name: "Booking & Appointments", icon: Calendar, count: 0 },
		{ id: "communication", name: "Communication", icon: HelpCircle, count: 0 },
		{ id: "payment", name: "Payment & Pricing", icon: CreditCard, count: 0 },
		{ id: "safety", name: "Safety & Trust", icon: Shield, count: 0 },
		{ id: "general", name: "General", icon: HelpCircle, count: 0 },
	];

	useEffect(() => {
		loadFaqs();
	}, []);

	useEffect(() => {
		filterFaqs();
	}, [faqs, searchTerm, selectedCategory]);

	const loadFaqs = async () => {
		try {
			setLoading(true);

			if (!user) {
				throw new Error("User not authenticated");
			}

			// Get the current session token
			const {
				data: { session },
			} = await supabase.auth.getSession();

			if (!session?.access_token) {
				throw new Error("No access token available");
			}

			// Use the deployed edge function
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-parent-faqs`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${session.access_token}`,
						"Content-Type": "application/json",
					},
				},
			);

			const { success, data, error } = await response.json();

			if (!success) {
				throw new Error(error || "Failed to load FAQs");
			}

			setFaqs(data || []);
		} catch (error) {
			console.error("Error loading FAQs:", error);
			setFaqs([]);
		} finally {
			setLoading(false);
		}
	};

	const filterFaqs = () => {
		let filtered = faqs;

		// Filter by search term
		if (searchTerm) {
			filtered = filtered.filter(
				(faq) =>
					faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
					faq.answer.toLowerCase().includes(searchTerm.toLowerCase()),
			);
		}

		// Filter by topic using actual topic field
		if (selectedCategory !== "all") {
			filtered = filtered.filter((faq) => faq.topic === selectedCategory);
		}

		setFilteredFaqs(filtered);
	};

	const getCategoryIcon = (categoryId: string) => {
		const category = categories.find((cat) => cat.id === categoryId);
		return category?.icon || HelpCircle;
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-sage-50 to-sage-100 p-6">
				<div className="max-w-7xl mx-auto">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						className="text-center py-20"
					>
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
						<p className="text-gray-600 font-karla">Loading FAQs...</p>
					</motion.div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-sage-50 to-sage-100">
			<div className="max-w-7xl mx-auto p-6">
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					className="mb-8"
				>
					<div className="flex items-center mb-6">
						<Button
							onClick={() => navigate("/parent-dashboard")}
							className="mr-4 bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 font-karla"
						>
							<ArrowLeft className="h-5 w-5 mr-2" />
							Back to Dashboard
						</Button>
					</div>

					<div className="text-center mb-8">
						<h1 className="text-4xl font-bold text-gray-900 mb-4 font-karla">
							Frequently Asked Questions
						</h1>
						<p className="text-lg text-gray-600 max-w-2xl mx-auto font-karla">
							Find answers to common questions about finding and working with
							doulas. Can't find what you're looking for? Contact our support
							team.
						</p>
					</div>
				</motion.div>

				{/* Search and Filters */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.1 }}
					className="mb-8"
				>
					<div className="glass-card p-6 rounded-2xl">
						{/* Search Bar */}
						<div className="relative mb-6">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
							<Input
								type="text"
								placeholder="Search FAQs..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="pl-10 h-12 text-lg border-0 bg-white/80 backdrop-blur-sm"
							/>
						</div>

						{/* Category Filters */}
						<div className="flex flex-wrap gap-3">
							{categories.map((category) => {
								const Icon = category.icon;
								const isActive = selectedCategory === category.id;
								const count =
									category.id === "all"
										? filteredFaqs.length
										: faqs.filter((faq) => faq.topic === category.id).length;

								return (
									<Button
										key={category.id}
										variant={isActive ? "default" : "outline"}
										onClick={() => setSelectedCategory(category.id)}
										className={`flex items-center space-x-2 px-4 py-2 rounded-full font-karla transition-all duration-200 ${
											isActive
												? "bg-primary-600 text-white shadow-lg"
												: "bg-white/80 text-gray-700 hover:bg-primary-50 border-primary-200"
										}`}
									>
										<Icon className="h-4 w-4" />
										<span>{category.name}</span>
										<Badge
											variant="secondary"
											className="ml-2 bg-primary-100 text-primary-700"
										>
											{count}
										</Badge>
									</Button>
								);
							})}
						</div>
					</div>
				</motion.div>

				{/* FAQ Grid */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.2 }}
					className="grid grid-cols-1 lg:grid-cols-2 gap-6"
				>
					{filteredFaqs.length === 0 ? (
						<div className="col-span-full">
							<div className="glass-card p-12 rounded-2xl text-center">
								<HelpCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
								<h3 className="text-xl font-semibold text-gray-900 mb-2 font-karla">
									No FAQs Found
								</h3>
								<p className="text-gray-600 font-karla">
									{searchTerm
										? `No FAQs match "${searchTerm}". Try adjusting your search terms.`
										: "No FAQs available in this category."}
								</p>
							</div>
						</div>
					) : (
						filteredFaqs.map((faq, index) => (
							<motion.div
								key={faq.id}
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.1 * index }}
							>
								<Card className="glass-card h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300">
									<CardHeader className="pb-4">
										<div className="flex items-start justify-between">
											<div className="flex items-center space-x-3">
												<div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
													<HelpCircle className="h-5 w-5 text-primary-600" />
												</div>
												<div>
													<CardTitle className="text-lg font-semibold text-gray-900 font-karla leading-relaxed">
														{faq.question}
													</CardTitle>
												</div>
											</div>
										</div>
									</CardHeader>
									<CardContent className="pt-0">
										<div className="prose prose-gray max-w-none">
											<p className="text-gray-700 leading-relaxed font-karla">
												{faq.answer}
											</p>
										</div>

										{/* FAQ Metadata */}
										<div className="mt-4 pt-4 border-t border-gray-100">
											<div className="flex items-center justify-between text-xs text-gray-500">
												<span className="font-karla">Parent FAQ</span>
												<span className="font-karla">
													Updated{" "}
													{new Date(faq.updated_at).toLocaleDateString()}
												</span>
											</div>
										</div>
									</CardContent>
								</Card>
							</motion.div>
						))
					)}
				</motion.div>
			</div>
		</div>
	);
}
